import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DeviceInfo } from '@ezugi/utils';

import { useIntl } from '../../hooks/useIntl';
import { gameActions, preloaderActions } from '../../store/actions';
import {
  isEvoPlayerSelector,
  isTapToPlayVisibleSelector,
  videoReadyStatusSelector,
} from '../../store/selectors';
import {
  isLoadedSelector,
  preloaderConfigSelector,
} from '../../store/selectors/preloader';

export type UsePreloaderOptions = {
  gameLoading?: boolean;
};

export const usePreloader = (options: UsePreloaderOptions = {}) => {
  const [ show, setShow ] = useState(true);

  const isVideoReady = useSelector(videoReadyStatusSelector);
  const isLoaded = useSelector(isLoadedSelector);
  const isEvoPlayer = useSelector(isEvoPlayerSelector);
  const isTapToPlayVisible = useSelector(isTapToPlayVisibleSelector);
  const { gameName, ...boPreloaderConfig } = useSelector(
    preloaderConfigSelector
  );

  const dispatch = useDispatch();

  const autoTapRunned = useRef(false);

  const { localeGameName, playLabel, infoMessage } = useIntl({ keys: {
    localeGameName: `games.${gameName}`,
    playLabel: 'general.tap_to_play',
    infoMessage: 'preloader.preloader_info_message',
  } });

  const isLoading = !isLoaded || (!(DeviceInfo.isIOS && !isEvoPlayer) && !isVideoReady) || Boolean(options.gameLoading);

  const preloaderTap = useCallback(() => {
    dispatch(preloaderActions.preloader.tap());
  }, [ dispatch ]);

  const onMount = useCallback(() => {
    dispatch(preloaderActions.preloader.show());
  }, [ dispatch ]);

  const preloaderHide = useCallback(() => {
    dispatch(preloaderActions.preloader.hide());
  }, [ dispatch ]);

  const onDestroy = useCallback(() => {
    dispatch(gameActions.game.start());
  }, [ dispatch ]);

  const onPlay = useCallback(() => {
    preloaderHide();
    setShow(false);

    if (DeviceInfo.isHandheld) {
      preloaderTap();
    }
  }, [ preloaderTap, preloaderHide, setShow ]);

  // This is the autoplay feature, no user interaction required to see the game
  // Note that there will be no video sound in auto-play by default
  // Exception: if user has reached the playpack session threshold in chrome://media-engagement
  // Also on iOS < 17 LOW POWER MODE the video will start-stop intermittently until user interact
  useEffect(() => {
    if (!isTapToPlayVisible && !isLoading && !autoTapRunned.current) {
      onPlay();
      autoTapRunned.current = true;
    }
  }, [ isLoading, isTapToPlayVisible, onPlay ]);

  return {
    boPreloaderConfig,
    localeGameName,
    playLabel,
    infoMessage,
    isTapToPlayVisible,
    isLoading,
    show,
    onPlay,
    onMount,
    onDestroy,
  };
};
